/**
 * Scroll reveal for `.fade-up`.
 *
 * `.fade-up` starts at `opacity: 0` in CSS, so if this never finishes those
 * sections are INVISIBLE — not un-animated, invisible. Everything below is
 * about making that impossible.
 *
 * Three ways it can fail, all handled:
 *
 *  1. NO IntersectionObserver (old browser, some webviews) → reveal everything
 *     immediately.
 *  2. prefers-reduced-motion → same, and skip the observer entirely. The CSS
 *     already neutralises the transition.
 *  3. THE PAGE IS NOT PRODUCING FRAMES. This is the one that is easy to miss.
 *     IntersectionObserver delivery is tied to the rendering lifecycle, so in a
 *     hidden tab it never fires at all — measured: `document.hidden === true`,
 *     `requestAnimationFrame` never runs, and a freshly-created observer on an
 *     element sitting in the viewport delivers nothing. Which means an article
 *     opened in a background tab, or rendered by a headless browser, a
 *     screenshot service, a print-to-PDF or a JS-executing crawler, is a blank
 *     page.
 *
 *     So there is a timer as well as an observer. It fires only if the observer
 *     has delivered NOTHING by then — when frames are being produced the
 *     in-viewport elements are reported synchronously on the first tick, which
 *     stands the timer down and keeps the scroll animation intact.
 *
 *     Adding `.in` is not sufficient in that state, and this is the subtle
 *     part: the cascade resolves to `opacity: 1`, but a CSS TRANSITION does not
 *     advance without frames either, so the element stays visually at 0. The
 *     fallback therefore also sets `.reveal-static` on <html>, which drops the
 *     transition (see components.css) so the end state applies at once.
 *
 * This is the same failure mode the design system flags for count-up counters
 * (§6.6): anything driven by the rendering lifecycle needs a `setTimeout` that
 * writes the final state.
 */
import { qsa, prefersReducedMotion } from './dom.js';

// Long enough that a slow first paint is not mistaken for a dead observer,
// short enough that a reader never sees the gap.
const FRAME_STARVATION_MS = 1200;

export function setupReveal() {
  const targets = qsa('.fade-up');
  if (!targets.length) return;

  const revealAll = () => targets.forEach((el) => el.classList.add('in'));

  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    revealAll();
    return;
  }

  let observerDelivered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      observerDelivered = true;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('in');
        // Reveal once. Re-animating on scroll-back is distracting on a news
        // page people scroll up and down.
        observer.unobserve(entry.target);
      }
    },
    // Fire slightly before the element reaches the viewport, so the transition
    // is finishing rather than starting as it becomes visible.
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  targets.forEach((el) => observer.observe(el));

  setTimeout(() => {
    if (observerDelivered) return;
    // No frames were produced. Give up on animating and make the content
    // visible — an unanimated page is a fine outcome, an empty one is not.
    observer.disconnect();
    // Before revealAll(), so the transition is already gone when `.in` lands.
    document.documentElement.classList.add('reveal-static');
    revealAll();
  }, FRAME_STARVATION_MS);
}
